package fr.manyverse.app.presentation.components.modules.timer

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Slider
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import fr.manyverse.app.domain.model.TableMember
import fr.manyverse.app.presentation.components.clearFocusOnTap
import fr.manyverse.app.presentation.components.modules.common.ParticipantSelectionMode
import fr.manyverse.app.presentation.components.modules.common.ParticipantSelector
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.timer_duration_hint
import manyverse.shared.generated.resources.timer_duration_label
import manyverse.shared.generated.resources.timer_launch
import manyverse.shared.generated.resources.timer_launch_title
import org.jetbrains.compose.resources.stringResource

/**
 * Dialog for configuring and launching a timer.
 */
@Composable
fun TimerLaunchDialog(
    showDialog: Boolean,
    players: List<TableMember>,
    labelOf: (TableMember) -> String = { it.userId.take(8) },
    selectedParticipantIds: Set<String>,
    allSelected: Boolean,
    isLoading: Boolean,
    errorMessage: String?,
    onToggleAllSelected: () -> Unit,
    onToggleParticipant: (String) -> Unit,
    onDismiss: () -> Unit,
    onConfirm: (durationSeconds: Int) -> Unit,
    modifier: Modifier = Modifier
) {
    // Duration state: text field in HH:MM:SS and slider in minutes
    var durationText by remember { mutableStateOf("00:05:00") }
    var sliderMinutes by remember { mutableFloatStateOf(5f) }
    var durationSeconds by remember { mutableStateOf(300) }

    val canSubmit = (allSelected || selectedParticipantIds.isNotEmpty()) &&
            durationSeconds in 1..28800 &&
            !isLoading

    if (showDialog) {
        AlertDialog(
            properties = DialogProperties(dismissOnBackPress = false, dismissOnClickOutside = false),
            onDismissRequest = {
                if (!isLoading) {
                    onDismiss()
                }
            },
            title = {
                Text(stringResource(Res.string.timer_launch_title))
            },
            text = {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState()),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    // Participants selection
                    ParticipantSelector(
                        players = players,
                        mode = ParticipantSelectionMode.Multiple(
                            selectedIds = selectedParticipantIds,
                            allSelected = allSelected,
                            onToggle = onToggleParticipant,
                            onToggleAll = onToggleAllSelected
                        ),
                        labelOf = labelOf,
                        isEnabled = !isLoading
                    )

                    // Duration input
                    Column {
                        Text(
                            text = stringResource(Res.string.timer_duration_label),
                            style = MaterialTheme.typography.labelMedium
                        )

                        // HH:MM:SS text field
                        OutlinedTextField(
                            value = durationText,
                            onValueChange = { newText ->
                                durationText = newText
                                val parsed = parseHhMmSs(newText)
                                if (parsed != null && parsed in 1..28800) {
                                    durationSeconds = parsed
                                    sliderMinutes = (parsed / 60f).coerceIn(1f, 10f)
                                } else if (parsed != null) {
                                    durationSeconds = parsed
                                    sliderMinutes = 10f
                                }
                            },
                            placeholder = { Text(stringResource(Res.string.timer_duration_hint)) },
                            singleLine = true,
                            enabled = !isLoading,
                            modifier = Modifier.fillMaxWidth().padding(top = 8.dp)
                        )

                        // Slider for quick selection (1-10 minutes)
                        Slider(
                            value = sliderMinutes,
                            onValueChange = { value ->
                                sliderMinutes = value
                                val newSeconds = (value * 60).toInt()
                                durationSeconds = newSeconds
                                durationText = formatSecondsToHhMmSs(newSeconds)
                            },
                            valueRange = 1f..10f,
                            steps = 8,
                            enabled = !isLoading,
                            modifier = Modifier.fillMaxWidth()
                        )

                        // Display current slider value
                        Text(
                            text = "${sliderMinutes.toInt()} min",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }

                    // Error message
                    if (errorMessage != null) {
                        Text(
                            text = errorMessage,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodySmall
                        )
                    }

                    // Loading indicator
                    if (isLoading) {
                        LinearProgressIndicator(
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
            },
            confirmButton = {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    TextButton(
                        onClick = onDismiss,
                        enabled = !isLoading
                    ) {
                        Text(stringResource(Res.string.cancel))
                    }
                    Button(
                        onClick = { onConfirm(durationSeconds) },
                        enabled = canSubmit
                    ) {
                        Text(stringResource(Res.string.timer_launch))
                    }
                }
            },
            modifier = modifier.clearFocusOnTap()
        )
    }
}

